import React, { useState, useEffect } from 'react';
import api, { API_URL } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ArrowLeftRight, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';

function StatusBadge({ status }) {
  const s = { successful: 'bg-emerald-100 text-emerald-800', pending: 'bg-amber-100 text-amber-800', failed: 'bg-red-100 text-red-800', reversed: 'bg-slate-100 text-slate-800' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s[status] || 'bg-slate-100 text-slate-800'}`}>{status}</span>;
}

function SettlementBadge({ txn }) {
  const mode = String(txn?.settlement_mode || '').toLowerCase();
  const status = String(txn?.settlement_status || '').toLowerCase();
  if (txn?.settled_directly_to_merchant || mode === 'flutterwave_split') {
    const label = status === 'pending' ? 'Split settlement pending' : 'Split-settled to merchant';
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">{label}</span>;
  }
  if (mode === 'jjuma_wallet') {
    const label = status === 'pending_wallet_fallback' ? 'Wallet settlement pending' : 'Paid via JJuma wallet';
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800">{label}</span>;
  }
  if (mode === 'manual') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800">Manual review</span>;
  }
  return null;
}

export default function Transactions() {
  const [data, setData] = useState({ transactions: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const params = new URLSearchParams({ page, limit: 20 });
    if (filter !== 'all') params.set('status', filter);
    api.get(`/transactions?${params}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [filter, page]);

  const exportCSV = async () => {
    try {
      const r = await api.get('/export/transactions/csv?scope=user', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `jjuma-pay_transactions_${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };
  const exportPDF = async () => {
    try {
      const r = await api.get('/export/transactions/pdf?scope=user', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `jjuma-pay_statement_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Transactions</h1>
          <p className="text-slate-500 text-sm mt-0.5">{data.total} total transactions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
            <Download size={14} className="mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={exportPDF} data-testid="export-pdf-btn">
            <FileText size={14} className="mr-1" /> PDF
          </Button>
          <Select value={filter} onValueChange={v => { setFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="txn-filter-select">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="successful">Successful</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>
          ) : data.transactions.length === 0 ? (
            <div className="py-12 text-center">
              <ArrowLeftRight size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No transactions yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Sender</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Amount</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Fee</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Net</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Link</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.transactions.map((t, i) => (
                    <TableRow key={i} className="hover:bg-slate-50/50" data-testid={`txn-row-${i}`}>
                      <TableCell>
                        <p className="text-sm font-medium text-slate-900">{t.sender_name}</p>
                        <p className="text-xs text-slate-400">{t.sender_email || t.sender_phone}</p>
                      </TableCell>
                      <TableCell className="font-semibold text-sm mono-nums text-emerald-700">{t.currency} {t.amount?.toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-slate-500 mono-nums">{t.currency} {t.fee?.toLocaleString()}</TableCell>
                      <TableCell className="text-sm mono-nums">{t.currency} {t.net_amount?.toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-slate-500">{t.payment_link_slug || '-'}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <StatusBadge status={t.status} />
                          <SettlementBadge txn={t} />
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 text-sm rounded-lg border bg-white hover:bg-slate-50 disabled:opacity-40">Previous</button>
          <span className="text-sm text-slate-500">Page {page} of {data.pages}</span>
          <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page === data.pages} className="px-3 py-1.5 text-sm rounded-lg border bg-white hover:bg-slate-50 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}
